import React, { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import {
  XIcon,
  PresentationChartBarIcon,
  CashIcon,
  CollectionIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/solid";
import { Link, useLocation } from "react-router-dom";
import useClickOutside from "../hooks/useClickOutside";
import { Button } from "./ui";
import { TbLayoutSidebarLeftCollapseFilled } from "react-icons/tb";
import { TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";
import { ROLE } from "../config";

const cookies = new Cookies();

function Sidebar({ show, onClose, isExpanded, expandRetractSidebar }) {
  const role = cookies.get(ROLE);
  const location = useLocation();

  const isMasterActive =
    location.pathname.startsWith("/petty-cash/entities") ||
    location.pathname.startsWith("/petty-cash/currencies");

  const [masterOpen, setMasterOpen] = useState(isMasterActive);

  useEffect(() => {
    if (isMasterActive) {
      setMasterOpen(true);
    }
  }, [isMasterActive]);

  const sidebarOptions = [
    {
      name: "",
      label: "Dashboard",
      icon: <PresentationChartBarIcon className="w-5" />,
    },
    {
      name: "petty-cash/fund-requests",
      label: "Fund Requests",
      icon: <CashIcon className="w-5" />,
    },
    ...(role === "COMPANY_ADMIN"
      ? [
          {
            isGroup: true,
            label: "Master",
            icon: <CollectionIcon className="w-5" />,
            isOpen: masterOpen,
            toggleOpen: () => setMasterOpen((prev) => !prev),
            isActive: isMasterActive,
            children: [
              {
                name: "petty-cash/entities",
                label: "Entity Master",
              },
              {
                name: "petty-cash/currencies",
                label: "Currency Master",
              },
            ],
          },
        ]
      : []),
    ...(role === "COMPANY_ADMIN"
      ? [
          {
            name: "petty-cash/settings",
            label: "Settings",
            icon: <CogIcon className="w-5" />,
          },
        ]
      : []),
  ];

  const isActive = (path) =>
    location.pathname === path
      ? "bg-blue-100 font-semibold text-primary "
      : "hover:bg-blue-50 font-semibold text-gray-700 hover:text-primary";

  const sidebarRef = useClickOutside(() => {
    onClose();
  });

  return (
    <div
      ref={sidebarRef}
      className={`bg-white shadow-lg duration-500 ease-in-out lg:block h-[calc(100vh-4rem)] p-4 overflow-auto top-16 z-[1001] right-0 lg:left-0 fixed ${
        show ? "right-0 translate-x-0" : " translate-x-[500px] lg:translate-x-0"
      } ${isExpanded ? "w-64" : "w-20"}`}
    >
      <div>
        <div
          className={
            "hidden lg:flex " +
            (isExpanded ? "justify-end mb-3 " : "justify-center mb-5")
          }
        >
          <Button
            variant={"ghost"}
            size={"icon"}
            onClick={expandRetractSidebar}
          >
            {isExpanded ? (
              <TbLayoutSidebarLeftCollapseFilled className="w-6 h-6 text-primary" />
            ) : (
              <TbLayoutSidebarRightCollapseFilled className="w-6 h-6 text-primary" />
            )}
          </Button>
        </div>
        <div className="block lg:hidden text-right" onClick={onClose}>
          <Button size={"icon"} variant={"ghost"}>
            <XIcon className="w-6 h-6 text-gray-700" />
          </Button>
        </div>

        <div className="gap-2 flex flex-col overflow-auto">
          {sidebarOptions.map((option, idx) => {
            if (option.isGroup) {
              return (
                <div key={`group-${idx}`} className="flex flex-col">
                  <button
                    type="button"
                    onClick={option.toggleOpen}
                    className={`flex items-center w-full p-2 rounded-lg cursor-pointer transition-colors ${
                      option.isActive
                        ? "bg-blue-50 text-primary font-semibold"
                        : "hover:bg-blue-50 font-semibold text-gray-700 hover:text-primary"
                    } ${isExpanded ? "justify-between" : "justify-center"}`}
                    title={!isExpanded ? option.label : undefined}
                  >
                    <div className="flex items-center space-x-3">
                      {option.icon}
                      {isExpanded && <span>{option.label}</span>}
                    </div>
                    {isExpanded && (
                      <span>
                        {option.isOpen ? (
                          <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                        )}
                      </span>
                    )}
                  </button>

                  {option.isOpen && isExpanded && (
                    <div className="mt-1 pl-4 flex flex-col space-y-1">
                      {option.children.map((child) => (
                        <Link
                          to={`/${child.name}`}
                          key={child.name}
                          onClick={() => {
                            if (window.innerWidth < 1024) onClose();
                          }}
                          className={`flex items-center space-x-3 py-2 px-3 rounded-lg text-sm transition-all ${isActive(
                            `/${child.name}`
                          )}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                          <span>{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                to={`/${option.name}`}
                key={option.name}
                onClick={() => {
                  if (window.innerWidth < 1024) onClose();
                }}
                className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${isActive(
                  `/${option.name}`
                )} ${isExpanded ? "justify-start" : "justify-center"}`}
                title={!isExpanded ? option.label : undefined}
              >
                {option.icon}
                {isExpanded && <span>{option.label}</span>}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;

